import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { BasketContext } from '~/providers/basketContext';
import { BasketQuote, QuoteAddress, QuoteResponse } from '~/lib/types/basket-types';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { useCallback, useContext, useMemo } from 'react';
import { QUOTE_API } from '~/lib/constants';
import { BasketSections } from '~/lib/types/enums';
import { captureException } from '~/lib/metrics';

/**
 * A hook used to work with the quote for the current basket item.
 */
export default function useQuote() {
  const { session } = useAnalytics();
  const { tenant, settings } = useTenant();
  const { state, dispatch } = useContext(BasketContext);

  const quote = useMemo(() => {
    return state.basket.items[state.currentItem]?.sections[BasketSections.QUOTE]?.payload as BasketQuote;
  }, [state.basket.items, state.currentItem]);

  const locations = useMemo(() => {
    return {
      origin: (state.basket.items[state.currentItem]?.sections[BasketSections.ORIGIN]?.payload as QuoteAddress)
        ?.country,
      destination: (
        state.basket.items[state.currentItem]?.sections[BasketSections.DESTINATION]?.payload as QuoteAddress
      )?.country
    };
  }, [state.basket.items, state.currentItem]);

  const errors = useMemo(() => {
    return quote?.errors || [];
  }, [quote]);

  const load = useCallback(async () => {
    try {
      const response = await fetchWithTenantAnalytics(
        tenant,
        settings,
        session,
        `${QUOTE_API}/quote/basket/${state.basket.id}?itemIds=${state.currentItem}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch quote http response code ${response.status}`);
      }

      const result = (await response.json()) as QuoteResponse;
      if (!result?.success || !result?.result) {
        throw new Error(`Failed fetch quote invalid api response ${result.error}`);
      }

      if (result.result.length > 0) {
        dispatch({
          type: 'UPDATE_SECTION_PAYLOAD',
          id: state.currentItem,
          name: BasketSections.QUOTE,
          payload: { ...result.result[0], currency: result.currency }
        });
      }
    } catch (e) {
      captureException(e, 'Failed to load quotes', {
        section: 'quote'
      });
    }
  }, [tenant, settings, session, state.basket.id, state.currentItem, dispatch]);

  return {
    /** The current origin and destination countries for the basket item. */
    locations,
    /** The full quote model for the current basket item. */
    quote,
    /** Function used to force a requote of the current basket item, note any unsaved basket changes will be not used when quoting */
    load,
    /** Any errors that occurred during the quote process. */
    quoteErrors: errors,
    /** The current quote for the basket item. */
    currency: quote?.currency
  };
}
